import React, { useState, useEffect, useCallback } from "react";
import {
  TextField,
  InputAdornment,
  IconButton,
  Icon,
  Drawer,
  Box,
  Typography,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Collapse,
  Chip,
  Switch,
  alpha,
  useTheme,
  InputBase,
  Paper,
} from "@mui/material";
import { SearchConfig, FilterGroup, FilterOption } from "../app.interface";
import { debounce } from "lodash";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";

export const AppSearch: React.FC<SearchConfig> = ({
  placeholder = "Search...",
  maxWidth = 400,
  onSearch,
  onFilterChange,
  filterGroups = [],
  defaultFilters = {},
}) => {
  const theme = useTheme();
  const [searchQuery, setSearchQuery] = useState("");
  const [filterQuery, setFilterQuery] = useState("");
  const [activeFilters, setActiveFilters] =
    useState<Record<string, any>>(defaultFilters);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState<string[]>([]);

  // Debounced search handler
  const debouncedSearch = useCallback(
    debounce((query: string) => {
      onSearch?.(query);
    }, 300),
    [onSearch]
  );

  useEffect(() => {
    debouncedSearch(searchQuery);
    return () => {
      debouncedSearch.cancel();
    };
  }, [searchQuery, debouncedSearch]);

  const handleFilterChange = (filterId: string, value: any) => {
    const newFilters = {
      ...activeFilters,
      [filterId]: value,
    };
    setActiveFilters(newFilters);
    onFilterChange?.(newFilters);
  };

  const toggleGroup = (groupId: string) => {
    setExpandedGroups((prev) =>
      prev.includes(groupId)
        ? prev.filter((id) => id !== groupId)
        : [...prev, groupId]
    );
  };

  const getFilteredGroups = () => {
    if (!filterQuery) return filterGroups;
    return filterGroups.filter(
      (group) =>
        group.label.toLowerCase().includes(filterQuery.toLowerCase()) ||
        group.options?.some((option) =>
          option.label.toLowerCase().includes(filterQuery.toLowerCase())
        )
    );
  };

  const renderFilterContent = (group: FilterGroup) => {
    switch (group.type) {
      case "select":
        return (
          <Box sx={{ mt: 1 }}>
            {group.options?.map((option) => (
              <ListItem
                key={option.id}
                dense
                component="div"
                onClick={() => handleFilterChange(group.id, option.value)}
                sx={{
                  borderRadius: 1,
                  mb: 0.5,
                  cursor: "pointer",
                  "&:hover": {
                    bgcolor: (theme) => alpha(theme.palette.action.hover, 0.1),
                  },
                  ...(activeFilters[group.id] === option.value && {
                    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
                    "&:hover": {
                      bgcolor: (theme) =>
                        alpha(theme.palette.primary.main, 0.12),
                    },
                  }),
                }}
              >
                {option.icon && (
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <Icon sx={{ color: option.color, fontSize: 20 }}>
                      {option.icon}
                    </Icon>
                  </ListItemIcon>
                )}
                <ListItemText
                  primary={option.label}
                  secondary={option.description}
                  primaryTypographyProps={{
                    variant: "body2",
                    fontWeight:
                      activeFilters[group.id] === option.value ? 600 : 400,
                  }}
                  secondaryTypographyProps={{
                    variant: "caption",
                    sx: { mt: 0.5 },
                  }}
                />
              </ListItem>
            ))}
          </Box>
        );
      case "boolean":
        return (
          <Box sx={{ mt: 1 }}>
            <ListItem dense>
              <ListItemText
                primary={group.label}
                secondary={group.description}
                primaryTypographyProps={{ variant: "body2" }}
                secondaryTypographyProps={{ variant: "caption" }}
              />
              <Switch
                edge="end"
                checked={!!activeFilters[group.id]}
                onChange={(e) => handleFilterChange(group.id, e.target.checked)}
              />
            </ListItem>
          </Box>
        );
      default:
        return null;
    }
  };

  const quickFilters = filterGroups.filter((group) => group.isQuickFilter);
  const activeFilterCount = Object.keys(activeFilters).length;

  return (
    <Box sx={{ width: "100%", maxWidth }}>
      <Paper
        elevation={0}
        sx={{
          display: "flex",
          alignItems: "center",
          px: 2,
          py: 1,
          bgcolor: "background.paper",
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2,
          "&:hover": {
            borderColor: "text.primary",
          },
        }}
      >
        <InputBase
          fullWidth
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder={placeholder}
          startAdornment={
            <InputAdornment position="start">
              <Icon sx={{ color: "text.secondary", fontSize: 20 }}>search</Icon>
            </InputAdornment>
          }
          sx={{
            fontFamily: "Montserrat",
            "& input": {
              py: 0.5,
              fontSize: "0.875rem",
              "&::placeholder": {
                color: "text.secondary",
                opacity: 0.8,
              },
            },
          }}
        />
        {quickFilters.map((filter) => (
          <Chip
            key={filter.id}
            label={filter.label}
            icon={filter.icon ? <Icon>{filter.icon}</Icon> : undefined}
            onClick={() =>
              handleFilterChange(filter.id, !activeFilters[filter.id])
            }
            sx={{
              ml: 1,
              bgcolor: activeFilters[filter.id]
                ? alpha(theme.palette.primary.main, 0.1)
                : "transparent",
              borderColor: activeFilters[filter.id]
                ? theme.palette.primary.main
                : theme.palette.divider,
              color: activeFilters[filter.id]
                ? theme.palette.primary.main
                : "text.secondary",
              "&:hover": {
                bgcolor: activeFilters[filter.id]
                  ? alpha(theme.palette.primary.main, 0.2)
                  : alpha(theme.palette.action.hover, 0.1),
              },
            }}
          />
        ))}
        <IconButton
          onClick={() => setDrawerOpen(true)}
          sx={{
            ml: 1,
            bgcolor:
              activeFilterCount > 0
                ? alpha(theme.palette.primary.main, 0.1)
                : "transparent",
            color: activeFilterCount > 0 ? "primary.main" : "text.secondary",
          }}
        >
          <Icon>tune</Icon>
          {activeFilterCount > 0 && (
            <Box
              sx={{
                position: "absolute",
                top: -2,
                right: -2,
                bgcolor: "primary.main",
                color: "primary.contrastText",
                borderRadius: "50%",
                width: 16,
                height: 16,
                fontSize: "0.75rem",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {activeFilterCount}
            </Box>
          )}
        </IconButton>
      </Paper>

      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{
          sx: {
            width: 320,
            maxWidth: "100%",
          },
        }}
      >
        <Box sx={{ p: 2 }}>
          <Typography variant="h6" fontWeight={600}>
            Filters
          </Typography>
          <TextField
            fullWidth
            size="small"
            placeholder="Search filters..."
            value={filterQuery}
            onChange={(e) => setFilterQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Icon fontSize="small">search</Icon>
                </InputAdornment>
              ),
            }}
            sx={{ mt: 2, mb: 1 }}
          />
          {activeFilterCount > 0 && (
            <Box sx={{ mt: 2, mb: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Active Filters
              </Typography>
              <Box
                sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.5 }}
              >
                {Object.entries(activeFilters).map(([key, value]) => {
                  const group = filterGroups.find((g) => g.id === key);
                  const option = group?.options?.find((o) => o.value === value);
                  return (
                    <Chip
                      key={key}
                      size="small"
                      label={`${group?.label}: ${option?.label || value}`}
                      onDelete={() => handleFilterChange(key, undefined)}
                      sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1) }}
                    />
                  );
                })}
                <Chip
                  size="small"
                  label="Clear All"
                  onDelete={() => {
                    setActiveFilters({});
                    onFilterChange?.({});
                  }}
                  sx={{
                    bgcolor: alpha(theme.palette.error.main, 0.1),
                    color: "error.main",
                  }}
                />
              </Box>
            </Box>
          )}
        </Box>
        <Divider />
        <List sx={{ flex: 1, overflow: "auto", py: 0 }}>
          {getFilteredGroups()
            .filter((group) => !group.isQuickFilter)
            .map((group) => (
              <React.Fragment key={group.id}>
                <ListItem
                  component="div"
                  onClick={() => toggleGroup(group.id)}
                  sx={{
                    py: 1.5,
                    px: 2,
                    borderLeft: 3,
                    cursor: "pointer",
                    borderColor: expandedGroups.includes(group.id)
                      ? "primary.main"
                      : "transparent",
                    "&:hover": {
                      bgcolor: (theme) =>
                        alpha(theme.palette.action.hover, 0.04),
                    },
                  }}
                >
                  {group.icon && (
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <Icon>{group.icon}</Icon>
                    </ListItemIcon>
                  )}
                  <ListItemText
                    primary={group.label}
                    secondary={group.description}
                    primaryTypographyProps={{
                      fontWeight: expandedGroups.includes(group.id) ? 600 : 400,
                    }}
                  />
                  <Icon>
                    {expandedGroups.includes(group.id)
                      ? "expand_less"
                      : "expand_more"}
                  </Icon>
                </ListItem>
                <Collapse in={expandedGroups.includes(group.id)}>
                  <Box sx={{ px: 2, pb: 2 }}>{renderFilterContent(group)}</Box>
                </Collapse>
                <Divider />
              </React.Fragment>
            ))}
        </List>
      </Drawer>
    </Box>
  );
};
